import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Box, CircularProgress } from '@mui/material'
import { fetchLatestScanResultsByNeedId } from '@/api/scansAPI'
import { categorizeResults } from '../utils/scanCategorization'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useStep } from '../hooks/useStep'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { COLORS } from '../theme'
import MilestoneScreen from '../components/MilestoneScreen'

export default function ScanMilestone() {
  const { fundingNeedId } = useOnboardingV2()
  const { token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''

  const { data: results } = useQuery({
    queryKey: ['scanResultsByNeedId', fundingNeedId],
    queryFn: () => fetchLatestScanResultsByNeedId(token, fundingNeedId!),
    enabled: !!token && !!fundingNeedId,
    retry: 3,
  })

  const selectableMatches = useMemo(() => {
    if (results === undefined) return null
    const cats = categorizeResults(results)
    return cats
      .filter((c) => c.key !== 'relational')
      .reduce((sum, c) => sum + c.fullCount, 0)
  }, [results])

  useStep({
    guard: {
      shouldSkip: selectableMatches !== null && selectableMatches < 3,
      targetStep: STEPS.SCAN_RESULTS,
    },
  })

  // Show spinner while results load to avoid flashing misleading content
  if (selectableMatches === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} sx={{ color: COLORS.primary }} />
      </Box>
    )
  }

  return (
    <MilestoneScreen
      title="Your matches are ready!"
      description="We've found funding opportunities that match your organisation and funding need. On the next screen you can select a match to generate your first application draft."
      warning={<><strong>Important:</strong> Fundsorter does not submit applications on your behalf. We help you find and draft — you review and submit.</>}
    />
  )
}
